import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UpgradeModal.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

/**
 * Shared Upgrade Modal — used across the entire app.
 * 
 * Props:
 *   show       - boolean: whether to display
 *   onClose    - function: called when modal is dismissed
 *   message    - string (optional): contextual message shown above the pricing
 *   tier       - 'pro' | 'business' (default: 'pro') — which tier to promote
 */
const UpgradeModal = ({ show, onClose, message, tier = 'pro' }) => {
  const { token } = useAuth();
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!show) return null;

  const isPro = tier === 'pro';
  const prices = isPro
    ? { monthly: '$1.99', annual: '$15.99', savings: '33%' }
    : { monthly: '$19.99', annual: '$179.99', savings: '25%' };

  const features = isPro
    ? [
        'Create unlimited discussion topics',
        'Unlimited replies',
        'Upload images in discussions',
        'Unlimited amp collection',
        'Unlimited AmpNebula Valuations\u2122 \u2014 AI market analysis',
        'PDF Valuation Report',
        <>Verified Owner &amp; Amp Collector badges</>,
      ]
    : [
        'Everything in Pro',
        'Priority support',
        'API access',
        'Team collaboration features',
        'Advanced analytics',
        'Custom branding',
      ];

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_URL}/billing/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ tier, period }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        // If user already has a subscription, redirect to portal
        if (data.redirect === 'portal') {
          const portalResp = await fetch(`${API_URL}/billing/create-portal`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          const portalData = await portalResp.json();
          if (portalData.url) {
            window.location.href = portalData.url;
            return;
          }
        }
        throw new Error(data.error || 'Failed to start checkout');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upgrade-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="upgrade-close" onClick={onClose}>&times;</button>
        
        <h2 className="upgrade-title">
          Upgrade to {isPro ? 'Pro' : 'Business'}
        </h2>

        {message && (
          <p className="upgrade-message">{message}</p>
        )}

        {/* Period toggle */}
        <div className="upgrade-period-toggle">
          <button 
            className={`upgrade-period-btn ${period === 'monthly' ? 'active' : ''}`}
            onClick={() => setPeriod('monthly')}
          >
            Monthly
          </button>
          <button 
            className={`upgrade-period-btn ${period === 'annual' ? 'active' : ''}`}
            onClick={() => setPeriod('annual')}
          >
            Annual <span className="upgrade-save-badge">Save {prices.savings}</span>
          </button>
        </div>

        {/* Price display */}
        <div className="upgrade-price-box">
          <span className="upgrade-price-amount">
            {period === 'monthly' ? prices.monthly : prices.annual}
          </span>
          <span className="upgrade-price-period">
            /{period === 'monthly' ? 'month' : 'year'}
          </span>
        </div>

        {/* Features */}
        <ul className="upgrade-features">
          {features.map((feature, i) => (
            <li key={i}>{feature}</li>
          ))}
        </ul>

        {error && <div className="upgrade-error">{error}</div>}

        <button 
          className="upgrade-checkout-btn" 
          onClick={handleCheckout}
          disabled={loading}
        >
          {loading ? 'Redirecting...' : `Subscribe — ${period === 'monthly' ? prices.monthly : prices.annual}/${period === 'monthly' ? 'mo' : 'yr'}`}
        </button>

        <button className="upgrade-later-btn" onClick={onClose}>
          Maybe Later
        </button>
      </div>
    </div>
  );
};

export default UpgradeModal;
